/**
 * Buyer location with a defined precedence, shared by every public discovery
 * surface (`/marketplace` in both modes, and `/marketplace/search`).
 *
 * Precedence, strongest first:
 *   1. A typed address the buyer geocoded — owned by the page, never touched here.
 *   2. `navigator.geolocation` — precise, but needs a permission grant and can
 *      hang or be denied.
 *   3. The CDN edge seed (`fs_geo` cookie) — approximate, instant, promptless.
 *   4. Nothing — the page keeps its continental-US default.
 *
 * Before this hook each page ran its own copy of the same silent
 * `getCurrentPosition` effect, which meant the map sat on a default centre until
 * the prompt resolved (and forever if it was denied). Seeding from the edge
 * removes that empty first paint without weakening the precise path: when a
 * grant lands, `source` flips to 'precise' and the coordinates upgrade in place.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  formatViewerLocationLabel,
  getViewerLocation,
  type ViewerLocation,
} from '../viewer-location';

export type ViewerLocationSource = 'precise' | 'approximate';

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface UseViewerLocationResult {
  /** Best known coordinates, or null when nothing has resolved yet. */
  coords: Coordinates | null;
  /** Display label — "your location" when precise, "Bozeman, MT" when seeded. */
  label: string;
  /** Which tier of the precedence chain produced `coords`. */
  source: ViewerLocationSource | null;
  /** The raw edge seed, for callers that want city/region/postal directly. */
  approximate: ViewerLocation | null;
  /** True while an explicit `requestPrecise()` is in flight. */
  loading: boolean;
  /**
   * Prompt for precise coordinates. Resolves to the coordinates on success and
   * null on denial/timeout/unsupported, so callers can show their own message.
   */
  requestPrecise: () => Promise<Coordinates | null>;
}

export interface UseViewerLocationOptions {
  /**
   * Raise the browser permission prompt on mount rather than waiting for the
   * buyer to press "Use my location". Off by default — see the mount effect for
   * why an unprompted dialog is normally the wrong trade. Pages whose entire
   * job is "what's near me" (e.g. /marketplace arrived at with no search in the
   * URL) opt in, because there the fix is the first thing the page needs.
   */
  promptOnMount?: boolean;
}

/** Matches the timeout the pages already used for their silent attempts. */
const GEOLOCATION_TIMEOUT_MS = 10000;

const LABEL_PRECISE = 'your location';

export function useViewerLocation(
  options: UseViewerLocationOptions = {},
): UseViewerLocationResult {
  // Read once: whether to prompt is a property of how the page was entered, so
  // a later re-render that flips the flag must not raise a second dialog.
  const promptOnMountRef = useRef(options.promptOnMount ?? false);

  // Seeded synchronously from the cookie so the very first render already has a
  // location — an effect here would reintroduce the blank first paint.
  const [approximate] = useState<ViewerLocation | null>(() =>
    getViewerLocation(),
  );

  const [precise, setPrecise] = useState<Coordinates | null>(null);
  const [loading, setLoading] = useState(false);

  // Guards a late silent resolution from clobbering a precise fix the buyer
  // explicitly requested in the meantime.
  const preciseRef = useRef<Coordinates | null>(null);

  const adoptPrecise = useCallback((coords: Coordinates) => {
    preciseRef.current = coords;
    setPrecise(coords);
  }, []);

  // Refresh on mount when the buyer has already granted permission to this
  // origin, and — only for callers that opt in via `promptOnMount` — when the
  // origin has yet to be asked. `getCurrentPosition` is not silent on an
  // ungranted origin: it raises the browser permission dialog. Firing that
  // unprompted is a poor trade on a page where the edge seed already answers
  // "where roughly am I", so by default the dialog stays reserved for the
  // explicit "Use my location" control (`requestPrecise`).
  //
  // A 'denied' origin is never touched: the call would fail immediately, and
  // browsers do not re-prompt once the buyer has said no.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation)
      return undefined;

    let cancelled = false;

    // `showLoading` only when the buyer will be watching a dialog they have to
    // answer — the already-granted path resolves without any interaction, and
    // flashing the button's spinner for it would be noise.
    const fetchPrecise = (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          if (showLoading) setLoading(false);
          if (cancelled || preciseRef.current) return;
          adoptPrecise({ lat: coords.latitude, lng: coords.longitude });
        },
        () => {
          /* Denied, unavailable or timed out — keep the seeded location. */
          if (showLoading) setLoading(false);
        },
        { timeout: GEOLOCATION_TIMEOUT_MS },
      );
    };

    // Without the Permissions API we cannot tell a granted origin from an
    // unasked one, so only the opt-in path may fire — it has already accepted
    // that a dialog can appear.
    if (!navigator.permissions?.query) {
      if (promptOnMountRef.current) fetchPrecise(true);
      return () => {
        cancelled = true;
      };
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'granted') fetchPrecise(false);
        else if (status.state === 'prompt' && promptOnMountRef.current)
          fetchPrecise(true);
      })
      .catch(() => {
        /* Permissions API rejected the descriptor — treat it as unknown. */
        if (!cancelled && promptOnMountRef.current) fetchPrecise(true);
      });

    return () => {
      cancelled = true;
    };
  }, [adoptPrecise]);

  const requestPrecise = useCallback(
    () =>
      new Promise<Coordinates | null>((resolve) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
          resolve(null);
          return;
        }
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const next = { lat: coords.latitude, lng: coords.longitude };
            adoptPrecise(next);
            setLoading(false);
            resolve(next);
          },
          () => {
            setLoading(false);
            resolve(null);
          },
          { timeout: GEOLOCATION_TIMEOUT_MS },
        );
      }),
    [adoptPrecise],
  );

  const coords =
    precise ??
    (approximate ? { lat: approximate.lat, lng: approximate.lng } : null);

  let source: ViewerLocationSource | null;
  if (precise) {
    source = 'precise';
  } else if (approximate) {
    source = 'approximate';
  } else {
    source = null;
  }

  let label: string;
  if (precise) {
    label = LABEL_PRECISE;
  } else if (approximate) {
    label = formatViewerLocationLabel(approximate);
  } else {
    label = '';
  }

  return { coords, label, source, approximate, loading, requestPrecise };
}
